import React, { useState, useEffect } from "react";

const VotePackageForm = ({
  isOpen,
  isLoading = false,
  initialData = null,
  onSubmit,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    type: "daily",
    votes: "",
    votesPerDay: "",
    durationDays: "7",
  });

  const [errors, setErrors] = useState({});

  // Populate form when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || "",
        price: initialData.price || "",
        type: initialData.type || "daily",
        votes: initialData.votes || "",
        votesPerDay: initialData.votesPerDay || "",
        durationDays: initialData.durationDays || "7",
      });
    } else {
      setFormData({
        name: "",
        price: "",
        type: "daily",
        votes: "",
        votesPerDay: "",
        durationDays: "7",
      });
    }
    setErrors({});
  }, [initialData, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Package name is required";
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = "Price must be greater than 0";
    }

    if (formData.type === "daily") {
      if (!formData.votes || parseInt(formData.votes) <= 0) {
        newErrors.votes = "Total votes is required for daily packages";
      }
    } else if (formData.type === "weekly") {
      if (!formData.votesPerDay || parseInt(formData.votesPerDay) <= 0) {
        newErrors.votesPerDay = "Votes per day is required for weekly packages";
      }
      if (!formData.durationDays || parseInt(formData.durationDays) <= 0) {
        newErrors.durationDays = "Duration days must be greater than 0";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare data based on type
    const submitData = {
      name: formData.name.trim(),
      price: parseFloat(formData.price),
      type: formData.type,
      durationDays: parseInt(formData.durationDays),
    };

    if (formData.type === "daily") {
      submitData.votes = parseInt(formData.votes);
      submitData.votesPerDay = null;
    } else {
      submitData.votesPerDay = parseInt(formData.votesPerDay);
      submitData.votes = null;
    }

    // If editing, include packageId
    if (initialData) {
      submitData.packageId = initialData.packageId;
    }

    onSubmit(submitData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg shadow-xl w-96 p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-white mb-4">
          {initialData ? "Edit Vote Package" : "Create Vote Package"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Package Name */}
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Package Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., K10, K100 Weekly"
              className={`w-full px-3 py-2 bg-gray-700 border ${
                errors.name ? "border-red-500" : "border-gray-600"
              } rounded text-white focus:outline-none focus:ring-2 focus:ring-red-600`}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Price (ZMW) *
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              placeholder="e.g., 10, 100"
              min="0"
              step="0.01"
              className={`w-full px-3 py-2 bg-gray-700 border ${
                errors.price ? "border-red-500" : "border-gray-600"
              } rounded text-white focus:outline-none focus:ring-2 focus:ring-red-600`}
            />
            {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Package Type *
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-red-600"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          {/* Conditional Fields based on type */}
          {formData.type === "daily" ? (
            // Daily Package Fields
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1">
                Total Votes *
              </label>
              <input
                type="number"
                name="votes"
                value={formData.votes}
                onChange={handleChange}
                placeholder="e.g., 100, 500"
                min="0"
                className={`w-full px-3 py-2 bg-gray-700 border ${
                  errors.votes ? "border-red-500" : "border-gray-600"
                } rounded text-white focus:outline-none focus:ring-2 focus:ring-red-600`}
              />
              {errors.votes && <p className="text-red-500 text-xs mt-1">{errors.votes}</p>}
            </div>
          ) : (
            // Weekly Package Fields
            <>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">
                  Votes Per Day *
                </label>
                <input
                  type="number"
                  name="votesPerDay"
                  value={formData.votesPerDay}
                  onChange={handleChange}
                  placeholder="e.g., 50, 100"
                  min="0"
                  className={`w-full px-3 py-2 bg-gray-700 border ${
                    errors.votesPerDay ? "border-red-500" : "border-gray-600"
                  } rounded text-white focus:outline-none focus:ring-2 focus:ring-red-600`}
                />
                {errors.votesPerDay && (
                  <p className="text-red-500 text-xs mt-1">{errors.votesPerDay}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">
                  Duration (days) *
                </label>
                <input
                  type="number"
                  name="durationDays"
                  value={formData.durationDays}
                  onChange={handleChange}
                  placeholder="e.g., 7, 30"
                  min="1"
                  className={`w-full px-3 py-2 bg-gray-700 border ${
                    errors.durationDays ? "border-red-500" : "border-gray-600"
                  } rounded text-white focus:outline-none focus:ring-2 focus:ring-red-600`}
                />
                {errors.durationDays && (
                  <p className="text-red-500 text-xs mt-1">{errors.durationDays}</p>
                )}
              </div>
            </>
          )}

          {/* Form Actions */}
          <div className="flex gap-3 mt-6 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors disabled:opacity-50"
            >
              {isLoading ? "Saving..." : initialData ? "Update Package" : "Create Package"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VotePackageForm;
